import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle, CheckCircle, Info } from "lucide-react";
import { CircularProgress } from "@mui/material";
import {
  DosageCalculatorProps,
  CalculatorResult,
} from "../dosageCalculators/types";

interface PeriodDelayOption {
  prescribeItemId: string;
  strenght: string;
  medicationType: string;
  name: string;
  medication: string;
  dosage: string;
  quantityFormula: (days: number) => string;
  duration: (days: number) => string;
  notes: string;
  startTiming: string;
  suitableFor: string[];
  contraindications: string[];
  isOffLabel?: boolean;
  isContraceptive?: boolean;
}

const VTE_RISK_FACTORS = [
  "history of blood clots",
  "family history of VTE",
  "overweight or obese",
  "current smoker",
  "aged over 35 and smoking",
  "varicose veins",
] as const;

const periodDelayOptions: PeriodDelayOption[] = [
  {
    prescribeItemId: "afbe62b7-121e-446e-b78f-ee1ce5e6c747",
    name: "Norethisterone",
    strenght: "5mg",
    medicationType:"tablets",
    medication: "Norethisterone",
    dosage: "Take 1 tablet three times daily",
    quantityFormula: (days) => {
      // 3 tablets per day for delay period + 3 days before
      const totalDays = days + 3;
      return `${totalDays * 3} tablets`;
    },
    duration: (days) => {
      return `${days + 3} days (start 3 days before period due)`;
    },
    startTiming: "Start at least 3 days before your expected period",
    notes:
      "Norethisterone is the only progestogen licensed in the UK to delay periods. Take 1 tablet three times daily. Start at least 3 days before your period is due. Period will typically start 2-3 days after stopping. Norethisterone is NOT a contraceptive — advise use of additional contraception (e.g. condoms) to avoid pregnancy. Note: Norethisterone is partly metabolised to ethinylestradiol, therefore it carries a VTE risk similar to combined hormonal contraceptives. Avoid in patients at increased risk of VTE. Common side effects include nausea, headache, and breast tenderness.",
    suitableFor: [
      "adults with no VTE risk factors",
      "individuals with regular cycles",
      "short-term use (maximum 17 days delay)",
    ],
    contraindications: [
      "pregnancy",
      "breast cancer",
      "severe liver disease",
      "history of blood clots",
      "family history of VTE",
      "undiagnosed vaginal bleeding",
      "overweight or obese",
      "current smoker",
      "aged over 35 and smoking",
      "varicose veins",
    ],
    isOffLabel: false,
    isContraceptive: false,
  },
  {
    prescribeItemId: "4bddf1b4-5e0c-44f2-9bf0-0bb232975a00",
    name: "Medroxyprogesterone Acetate (Provera)",
    strenght: "10mg",
    medicationType:"tablets",
    medication: "Medroxyprogesterone Acetate (Provera)",
    dosage: "Take 1 tablet three times daily",
    quantityFormula: (days) => {
      // 3 tablets per day for delay period + 3 days before
      const totalDays = days + 3;
      return `${totalDays * 3} tablets`;
    },
    duration: (days) => {
      return `${days + 3} days (start 3 days before period due)`;
    },
    startTiming: "Start at least 3 days before your expected period",
    notes:
      "Off-label use: Medroxyprogesterone is not licensed for period delay but is a suitable alternative progestogen, particularly for individuals with contraindications to oestrogens or at increased risk of VTE (e.g. overweight, smokers, aged over 35, history/family history of blood clots). Medroxyprogesterone is NOT a contraceptive — advise use of additional contraception (e.g. condoms). Oral medroxyprogesterone may inhibit ovulation so fertility can be affected; the time for fertility to return to normal varies. Periods usually resume within 3 days of stopping. Breakthrough bleeding can occur. Ref: SPS NHS — Choosing a medicine to delay periods.",
    suitableFor: [
      "individuals at increased risk of VTE",
      "individuals with contraindications to oestrogens",
      "overweight or obese individuals",
      "smokers or those aged over 35",
      "those with personal or family history of blood clots",
    ],
    contraindications: [
      "pregnancy",
      "breast cancer",
      "severe liver disease",
      "undiagnosed vaginal bleeding",
    ],
    isOffLabel: true,
    isContraceptive: false,
  },
  {
    prescribeItemId: "k9x_00000000-0000-0000-0000-abc000000002",
    name: "Combined Oral Contraceptive",
    strenght: "10mg",
    medicationType:"tablets",
    medication: "Combined Oral Contraceptive (monophasic)",
    dosage: "Take 1 tablet daily without a break between packs",
    quantityFormula: (days) => {
      // Days of delay rounded up to nearest pack (21 tablets per pack)
      const packs = Math.ceil(days / 21);
      return `${packs} pack(s) (${packs * 21} tablets)`;
    },
    duration: (days) => {
      return `${days} days (run packs together without break)`;
    },
    startTiming:
      "Start immediately after finishing current pack — skip the pill-free/placebo week",
    notes:
      "Only suitable for individuals already taking a combined oral contraceptive pill. Must be a monophasic pill to run back-to-back. Start the next pack immediately after finishing the current pack, skipping the usual 7-day break. The withdrawal bleed will start approximately 3 days after stopping the pill. Monophasic pills can be taken continuously but breakthrough bleeding is more common in the first few months and risk increases with prolonged continuous use. This is an existing contraceptive — no additional contraception needed. Ref: SPS NHS — Choosing a medicine to delay periods.",
    suitableFor: [
      "individuals already on a monophasic combined pill",
      "longer delay periods",
      "regular pill users",
    ],
    contraindications: [
      "not already taking combined pill",
      "history of migraines with aura",
      "history of blood clots",
      "aged over 35 and smoking",
      "uncontrolled high blood pressure",
    ],
    isOffLabel: true,
    isContraceptive: true,
  },
];

export function PeriodDelayCalculator({
  onComplete,
  onCancel,
  patientInfo,
  isDarkMode = false,
}: DosageCalculatorProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [delayDays, setDelayDays] = useState<string>("");
  const [onCombinedPill, setOnCombinedPill] = useState<boolean>(false);
  const [hasContraindications, setHasContraindications] = useState<string[]>(
    [],
  );
  const [hasVTERiskFactors, setHasVTERiskFactors] = useState<string[]>([]);
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const handleContinue = () => {
    if (step === 1 && delayDays) {
      setStep(2);
    } else if (step === 2) {
      setStep(3);
    }
  };

  const handleComplete = () => {
    const option = periodDelayOptions.find(
      (opt) => opt.prescribeItemId === selectedOption,
    );
    if (!option) return;

    const days = parseInt(delayDays);

    const offLabelPrefix = option.isOffLabel ? "[OFF-LABEL] " : "";
    const contraceptiveNote = !option.isContraceptive
      ? " IMPORTANT: This medication is NOT a contraceptive — advise additional contraception."
      : "";

    const result: CalculatorResult = {
      medication: `${offLabelPrefix}${option.medication}`,
      dosage: option.dosage,
      quantity: option.quantityFormula(days),
      strength: option.strenght,
      medicationType: option.medicationType,
      duration: option.duration(days),
      notes: `${option.startTiming}. ${option.notes}${contraceptiveNote}`,
      prescribedItemId: option.prescribeItemId,
    };

    setIsLoading(true);
    setTimeout(() => {
      onComplete([result]);
      setIsLoading(false);
    }, 1000);
  };

  const patientHasVTERisk = hasVTERiskFactors.length > 0;

  const getFilteredOptions = () => {
    return periodDelayOptions.filter((option) => {
      // If patient is on combined pill, show that option; otherwise hide it
      if (option.prescribeItemId === "combined_pill" && !onCombinedPill) {
        return false;
      }

      // If patient has VTE risk factors, norethisterone should be contraindicated
      // (per SPS: norethisterone is metabolised to ethinylestradiol, carries VTE risk)
      if (
        option.prescribeItemId === "afbe62b7-121e-446e-b78f-ee1ce5e6c747" &&
        patientHasVTERisk
      ) {
        return false;
      }

      // Check for contraindications
      const hasBlockingContraindication = hasContraindications.some(
        (contra) =>
          contra &&
          option.contraindications.some(
            (optContra) =>
              optContra &&
              optContra.toLowerCase().includes(contra.toLowerCase()),
          ),
      );

      if (hasBlockingContraindication) {
        return false;
      }
      // Check allergies
      if (patientInfo?.allergies) {
        const allergyNames = patientInfo.allergies
          .filter((a) => a?.allergyName)
          .map((a) => a.allergyName.toLowerCase());
        if (
          allergyNames.some((allergy) =>
            option.medication.toLowerCase().includes(allergy),
          )
        ) {
          return false;
        }
      }

      return true;
    });
  };

  const toggleContraindication = (contraindication: string) => {
    setHasContraindications((prev) =>
      prev.includes(contraindication)
        ? prev.filter((c) => c !== contraindication)
        : [...prev, contraindication],
    );
  };

  const toggleVTERiskFactor = (factor: string) => {
    setHasVTERiskFactors((prev) =>
      prev.includes(factor)
        ? prev.filter((f) => f !== factor)
        : [...prev, factor],
    );
  };

  return (
    <div
      className={`flex flex-col h-full ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
    >
      {/* Scrollable content - flex-1 to push footer down when overflowing */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-6 pr-1">
        {/* Progress Indicator */}
        <div className="flex items-center gap-2">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= s
                    ? "bg-pink-600 text-white"
                    : isDarkMode
                      ? "bg-gray-700 text-gray-400"
                      : "bg-gray-200 text-gray-500"
                }`}
              >
                {s}
              </div>
              {s < 3 && (
                <div
                  className={`flex-1 h-1 ${
                    step > s
                      ? "bg-pink-600"
                      : isDarkMode
                        ? "bg-gray-700"
                        : "bg-gray-200"
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Delay Duration */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="m-1">
              <Label
                htmlFor="delay-days"
                className={`text-base font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
              >
                How many days does the patient want to delay their period?
              </Label>
              <p
                className={`text-sm mt-1 mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Enter the number of days to delay the period
              </p>
              <Input
                id="delay-days"
                type="number"
                min="1"
                max="21"
                step="1"
                value={delayDays}
                placeholder="e.g., 7"
                className="text-lg"
                onKeyDown={(e) => {
                  if ([".", "e", "E", "+", "-"].includes(e.key)) {
                    e.preventDefault(); // 🚫 block unwanted characters
                  }
                }}
                onChange={(e) => {
                  setDelayDays(e.target.value);
                }}
              />
            </div>

            <div
              className={`rounded-lg p-4 ${isDarkMode ? "bg-amber-900/20 border border-amber-700" : "bg-amber-50 border border-amber-200"}`}
            >
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div
                  className={`text-sm ${isDarkMode ? "text-amber-300" : "text-amber-800"}`}
                >
                  <p className="font-medium mb-1">
                    Important Information (SPS NHS Guidance)
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>
                      Norethisterone should be started at least 3 days before
                      the expected period
                    </li>
                    <li>
                      Period typically starts 2-3 days after stopping medication
                    </li>
                    <li>Efficacy can vary between individuals</li>
                    <li>
                      Maximum recommended delay with norethisterone is 17 days
                    </li>
                    <li>
                      Norethisterone is NOT a contraceptive — additional
                      contraception must be used
                    </li>
                    <li>
                      Norethisterone is the only progestogen licensed in the UK
                      to delay periods; other options are off-label
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {delayDays && parseInt(delayDays) > 17 && (
              <div
                className={`rounded-lg p-4 ${isDarkMode ? "bg-red-900/20 border border-red-700" : "bg-red-50 border border-red-200"}`}
              >
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div
                    className={`text-sm ${isDarkMode ? "text-red-300" : "text-red-800"}`}
                  >
                    <p className="font-medium mb-1">Warning</p>
                    <p>
                      Delaying a period for more than 17 days is not typically
                      recommended with norethisterone or medroxyprogesterone.
                      For longer delays, consider a combined oral contraceptive
                      (back-to-back) if the patient is already taking one, or
                      discuss timing with the patient.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Medical History & VTE Risk Assessment */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <Label
                className={`text-base font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
              >
                Current contraception
              </Label>
              <div className="mt-2 space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="combined-pill"
                    checked={onCombinedPill}
                    onCheckedChange={(checked) => setOnCombinedPill(!!checked)}
                  />
                  <Label
                    htmlFor="combined-pill"
                    className="text-sm font-normal cursor-pointer"
                  >
                    Currently taking a monophasic combined oral contraceptive
                    pill
                  </Label>
                </div>
              </div>
            </div>

            {/* VTE Risk Factors — per SPS guidance */}
            <div
              className={`pt-3 mt-1 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
            >
              <Label
                className={`text-base font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
              >
                VTE risk factor assessment
              </Label>
              <p
                className={`text-sm mt-1 mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Norethisterone is partly metabolised to ethinylestradiol and
                carries VTE risk. If any VTE risk factors apply,
                medroxyprogesterone should be used instead (SPS NHS guidance).
              </p>
              <div className="space-y-2">
                {VTE_RISK_FACTORS.map((factor) => (
                  <div key={factor} className="flex items-center space-x-2">
                    <Checkbox
                      id={`vte-${factor}`}
                      checked={hasVTERiskFactors.includes(factor)}
                      onCheckedChange={() => toggleVTERiskFactor(factor)}
                    />
                    <Label
                      htmlFor={`vte-${factor}`}
                      className="text-sm font-normal cursor-pointer capitalize"
                    >
                      {factor}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* General contraindications */}
            <div
              className={`pt-3 mt-1 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
            >
              <Label
                className={`text-base font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
              >
                Other contraindications
              </Label>
              <p
                className={`text-sm mt-1 mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Select any conditions that apply to the patient
              </p>
              <div className="space-y-2">
                {[
                  "pregnancy",
                  "breast cancer",
                  "severe liver disease",
                  "undiagnosed vaginal bleeding",
                  "migraines with aura",
                  "uncontrolled high blood pressure",
                ].map((condition) => (
                  <div key={condition} className="flex items-center space-x-2">
                    <Checkbox
                      id={condition}
                      checked={hasContraindications.includes(condition)}
                      onCheckedChange={() => toggleContraindication(condition)}
                    />
                    <Label
                      htmlFor={condition}
                      className="text-sm font-normal cursor-pointer capitalize"
                    >
                      {condition}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {onCombinedPill && (
              <div
                className={`rounded-lg p-4 ${isDarkMode ? "bg-blue-900/20 border border-blue-700" : "bg-blue-50 border border-blue-200"}`}
              >
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div
                    className={`text-sm ${isDarkMode ? "text-blue-300" : "text-blue-800"}`}
                  >
                    <p className="font-medium mb-1">
                      Combined pill option available
                    </p>
                    <p>
                      The patient can delay their withdrawal bleed by running
                      monophasic packs back-to-back (skipping the pill-free
                      week). This is often the simplest option for patients
                      already on a combined pill. Breakthrough bleeding is more
                      common in the first few months.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {patientHasVTERisk && (
              <div
                className={`rounded-lg p-4 ${isDarkMode ? "bg-amber-900/20 border border-amber-700" : "bg-amber-50 border border-amber-200"}`}
              >
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div
                    className={`text-sm ${isDarkMode ? "text-amber-300" : "text-amber-800"}`}
                  >
                    <p className="font-medium mb-1">
                      VTE risk identified — Norethisterone not recommended
                    </p>
                    <p>
                      Norethisterone is partly metabolised to ethinylestradiol
                      (1mg norethisterone ≈ 4-6mcg ethinylestradiol). Per SPS
                      NHS guidance, medroxyprogesterone acetate (Provera) 10mg
                      TDS should be used instead for patients at increased risk
                      of VTE. This is an off-label use.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {hasContraindications.length > 0 && (
              <div
                className={`rounded-lg p-4 ${isDarkMode ? "bg-amber-900/20 border border-amber-700" : "bg-amber-50 border border-amber-200"}`}
              >
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div
                    className={`text-sm ${isDarkMode ? "text-amber-300" : "text-amber-800"}`}
                  >
                    <p className="font-medium mb-1">Contraindications noted</p>
                    <p>
                      Some medication options will be filtered out based on the
                      selected contraindications. Please review carefully before
                      prescribing.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Medication Selection */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <Label
                className={`text-base font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
              >
                Select the most appropriate medication
              </Label>
              <p
                className={`text-sm mt-1 mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Based on {delayDays} days delay
                {onCombinedPill && " (currently on combined pill)"}
                {patientHasVTERisk && " — VTE risk identified"}
              </p>

              {getFilteredOptions().length === 0 ? (
                <div
                  className={`rounded-lg p-4 ${isDarkMode ? "bg-red-900/20 border border-red-700" : "bg-red-50 border border-red-200"}`}
                >
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div
                      className={`text-sm ${isDarkMode ? "text-red-300" : "text-red-800"}`}
                    >
                      <p className="font-medium mb-1">No suitable options</p>
                      <p>
                        Based on the contraindications selected, there are no
                        safe medication options available. Please review the
                        patient's medical history or consider alternative
                        approaches.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <RadioGroup
                  value={selectedOption}
                  onValueChange={setSelectedOption}
                >
                  <div className="space-y-3">
                    {getFilteredOptions().map((option) => {
                      const days = parseInt(delayDays);
                      return (
                        <Card
                          key={option.prescribeItemId}
                          className={`cursor-pointer transition-colors ${
                            selectedOption === option.prescribeItemId
                              ? isDarkMode
                                ? "border-pink-500 bg-pink-900/30"
                                : "border-pink-600 bg-pink-50"
                              : isDarkMode
                                ? "hover:bg-gray-800/30"
                                : "hover:bg-gray-50"
                          }`}
                          onClick={() =>
                            setSelectedOption(option.prescribeItemId)
                          }
                        >
                          <CardHeader className="p-4">
                            <div className="flex items-start gap-3">
                              <RadioGroupItem
                                value={option.prescribeItemId}
                                id={option.prescribeItemId}
                                className="mt-1"
                              />
                              <div className="flex-1">
                                <CardTitle className="text-base flex items-center gap-2">
                                  {[option?.name, option?.strenght, option?.medicationType]
                                  .filter(Boolean)
                                  .join(" ")}
                                  {option.isOffLabel && (
                                    <span
                                      className={`text-xs px-2 py-0.5 rounded-full font-normal ${
                                        isDarkMode
                                          ? "bg-amber-900/40 text-amber-300 border border-amber-700"
                                          : "bg-amber-100 text-amber-800 border border-amber-300"
                                      }`}
                                    >
                                      Off-label
                                    </span>
                                  )}
                                </CardTitle>
                                <div
                                  className={`mt-2 space-y-1 text-sm ${isDarkMode ? "text-gray-400" : "text-muted-foreground"}`}
                                >
                                  <div>
                                    <span className="font-medium">
                                      Medication:
                                    </span>{" "}
                                    {option.medication}
                                  </div>
                                  <div>
                                    <span className="font-medium">Dosage:</span>{" "}
                                    {option.dosage}
                                  </div>
                                  <div>
                                    <span className="font-medium">
                                      Quantity:
                                    </span>{" "}
                                    {option.quantityFormula(days)}
                                  </div>
                                  <div>
                                    <span className="font-medium">
                                      When to start:
                                    </span>{" "}
                                    {option.startTiming}
                                  </div>
                                  {!option.isContraceptive && (
                                    <div
                                      className={`font-medium ${isDarkMode ? "text-amber-300" : "text-amber-700"}`}
                                    >
                                      Not a contraceptive — additional
                                      contraception required
                                    </div>
                                  )}
                                  <div
                                    className={`text-xs mt-2 pt-2 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
                                  >
                                    {option.notes}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </CardHeader>
                        </Card>
                      );
                    })}
                  </div>
                </RadioGroup>
              )}
            </div>

            {/* SPS reference note */}
            <div
              className={`rounded-lg p-3 ${isDarkMode ? "bg-gray-800/50 border border-gray-700" : "bg-gray-50 border border-gray-200"}`}
            >
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                <p
                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                >
                  Guidance based on SPS NHS — "Choosing a medicine to delay
                  periods" (May 2024). Norethisterone is the only licensed
                  option; all other medicines for this indication are off-label.
                  Advise patients that efficacy varies between individuals.
                </p>
              </div>
            </div>

            {selectedOption && (
              <div
                className={`rounded-lg p-4 ${isDarkMode ? "bg-green-900/20 border border-green-700" : "bg-green-50 border border-green-200"}`}
              >
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div
                    className={`text-sm ${isDarkMode ? "text-green-300" : "text-green-800"}`}
                  >
                    <p className="font-medium mb-1">Ready to prescribe</p>
                    <p>
                      The selected medication and dosage will be added to the
                      prescription when you click "Add to Prescription".
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* end scrollable content */}

      {/* Flexible footer - flows naturally with content, sticks when content overflows */}
      <div
        className={`flex items-center gap-3 pt-4 border-t flex-shrink-0 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
      >
        <Button variant="outline" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
        {step > 1 && (
          <Button
            variant="outline"
            onClick={() => setStep((s) => Math.max(1, s - 1) as 1 | 2 | 3)}
            className="flex-1"
          >
            Back
          </Button>
        )}
        {step < 3 ? (
          <Button
            onClick={handleContinue}
            disabled={step === 1 && !delayDays}
            className="flex-1 bg-pink-600 text-white hover:bg-pink-700"
          >
            Continue
          </Button>
        ) : (
          <Button
            onClick={handleComplete}
            disabled={!selectedOption || isLoading}
            className="flex-1 bg-green-600 text-white hover:bg-green-700"
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} color="inherit" className="mr-2" />
                Adding...
              </>
            ) : (
              "Add to Prescription"
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
